import React from 'react'
import cx from 'classnames'
import * as R from 'ramda'
import { t } from 'ttag'

import { Icon, SearchIcon } from 'components/shared/Icon/Icon'

import styles from './SearchWidget.module.scss'

type SearchButtonProps = {
  onClick: React.MouseEventHandler<HTMLButtonElement>
}

const SearchButton = ({ onClick }: SearchButtonProps) => (
  <button className={styles.query} onClick={onClick}>
    <Icon className={styles.lens} icon={SearchIcon} size={26} />
  </button>
)

type CrossButtonProps = {
  onClick: React.MouseEventHandler<HTMLButtonElement>
}

const CrossButton = ({ onClick }: CrossButtonProps) => (
  <button className={styles.cross} onClick={onClick}>
    <svg
      className={styles.icon}
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="m8.75 8.75-8-8m0 8 8-8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  </button>
)

type SearchWidgetProps = {
  className?: string
  onChange: (value: string) => void
  placeholder?: string
  query?: string
}

export const SearchWidget = ({
  className,
  query = '',
  placeholder = t`search`,
  onChange,
}: SearchWidgetProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(R.equals(query, ''))

  const ref = React.useRef<HTMLInputElement>(null)

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target

      onChange(value)
    },
    [onChange],
  )

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { value } = event.target
      if (!value) setIsCollapsed(true)
    },
    [setIsCollapsed],
  )

  const handleInputClear = React.useCallback(() => {
    const value = ''
    ref.current?.focus()
    onChange(value)
  }, [onChange])

  const toggleCollapsed = React.useCallback(() => {
    setIsCollapsed(R.not)
  }, [setIsCollapsed])

  React.useEffect(() => {
    let timeout

    if (!isCollapsed) {
      timeout = window.setTimeout(() => {
        ref.current?.focus()
      }, 100)
    }

    return () => {
      window.clearTimeout(timeout)
    }
  }, [isCollapsed])

  return (
    <div
      role="search"
      className={cx(styles.search, {
        [styles.collapsed]: Boolean(isCollapsed),
        [className]: Boolean(className),
      })}
    >
      <SearchButton onClick={toggleCollapsed} />
      <input
        className={styles.input}
        placeholder={placeholder}
        ref={ref}
        type="search"
        value={query}
        onChange={handleInputChange}
        onBlur={handleBlur}
      />
      {query && <CrossButton onClick={handleInputClear} />}
    </div>
  )
}
